import { Box, Card, IconButton, Skeleton, Tooltip, Typography, useTheme } from '@mui/material'
import PropTypes from 'prop-types'
import React from 'react'
import { Icons } from '../../utils/utilities'

/**
/**
 * MARKDOWN styled code button
 *
 * @param {Object} props
 * @param {(String )} props.value - Count value
 * @param {(String )} props.label - Label
 * @param {(React.ReactNode|Element )} props.icon - Mui Icon
 */

const iconFirstSkeleton = ( color ) => (
    <Box
        display="flex"
        width="100%"
        alignItems={{ md: "center", xs: "flex-start" }}
        flexDirection={{ md: "row", xs: "column" }}
        gap="10px"
        padding="10px"
    >
        {/* Icon Skeleton */}
        <Skeleton
            variant="circular"
            width={35}
            height={35}
            animation="wave"
            sx={{ aspectRatio: "1 / 1" }}
        />

        <Box
            display="flex"
            justifyContent="space-between"
            flexGrow={1}
            flexDirection="column"
            width="100%"
            gap="10px"
            overflow="hidden"
        >
            {/* Primary label + value */}
            <Box>
                <Skeleton width="40%" height={14} animation="wave" />
                <Skeleton width="70%" height={20} animation="wave" />
            </Box>

            {/* (
            <Box width="100%" display="flex" justifyContent="space-between" gap="10px">
                <Skeleton width="40%" height={14} animation="wave" />
                <Skeleton width="30%" height={16} animation="wave" />
            </Box>
            ) */}
        </Box>

    </Box>
)

const stackedSkeleton = ( color ) => (
    <Box
        height="100%"
        position="relative"
        display="flex"
        alignItems="stretch"
        flexDirection={{ md: "row", xs: "column" }}
    >
        {/* Floating corner icon skeleton */}
        <Box position="absolute" top="10px" right="10px">
            <Skeleton variant="circular" width={24} height={24} animation="wave" />
        </Box>

        <Box
            height="100%"
            padding="10px"
            flexGrow={1}
        >
            <Box
                display="flex"
                height="100%"
                justifyContent="space-between"
                flexDirection="column"
                gap="20px"
                overflow="hidden"
            >
                {/* Label + Main value */}
                <Box>
                    <Skeleton width="35%" height={12} animation="wave" />
                    <Skeleton width="65%" height={24} animation="wave" />
                </Box>

                {/* Secondary row */}
                <Box display="flex" justifyContent="space-between" gap="10px" width="100%">
                    <Skeleton width="35%" height={12} animation="wave" />
                    <Skeleton width="25%" height={16} animation="wave" />
                </Box>
            </Box>

        </Box>
    </Box>
)

const CountCard = ( { variant = "icon-first", secondary, secondaryLabel, onClick, icon, label, value, color = "primary", loading = false, error = false, onRetry, highlightBorder = false } ) => {

    const { palette, border } = useTheme()

    return (
        <Card onClick={( onClick && typeof onClick === 'function' ) && onClick} elevation={0} sx={{ border: border[1], borderColor: highlightBorder ? palette?.[color] ? palette?.[color].main : color || palette.borderColor : undefined, cursor: ( onClick && typeof onClick === 'function' ) ? "pointer" : "default", "&:hover .on-hover": { color: palette.text.primary } }}>
            {!loading && variant === 'icon-first' && <Box display="flex" width='100%' height="100%" alignItems={{ md: "center", xs: "flex-start" }} flexDirection={{ md: "row", xs: "column" }} gap="10px" padding="10px">
                <Typography fontSize="20px" display="flex" alignItems="center" justifyContent="center" padding="10px" borderRadius="5px" textAlign="center" color={palette?.[color] ? palette?.[color].main : color || palette.primary.main} bgcolor={( palette?.[color] ? palette?.[color].main : color || palette.primary.main ) + 22} sx={{ width: "35px", height: "35px", aspectRatio: "1 / 1" }}>{icon}</Typography>
                <Box display="flex" justifyContent="space-between" flexGrow={1} flexDirection="column" width="100%" gap="10px" overflow="hidden">
                    <Box display="flex" flexDirection="column" justifyContent="flex-end" flexGrow={1} component="div">
                        <Typography display="block" maxWidth="80%" fontSize="12px" fontWeight="500" component="span" color="textSecondary">{label}</Typography>
                        <Tooltip disableInteractive placement='top-start' title={value}>
                            <Box color={palette.common.font} display="block" fontWeight="700" overflow="hidden" component="span" fontSize="16px">{value}</Box>
                        </Tooltip>
                    </Box>
                    {secondary &&
                        <Box width="100%" component="div" display="flex" gap="10px" justifyContent="space-between">
                            {secondaryLabel && <Typography maxWidth="80%" display="block" fontSize={secondary ? "12px" : "14px"} fontWeight="400" component="span" color="textSecondary">{secondaryLabel} </Typography>}
                            <Tooltip disableInteractive placement='top-end' title={secondary}>
                                <Box color={palette.common.font} display="block" fontWeight="700" overflow="hidden" component="span" fontSize="14px">{secondary}</Box>
                            </Tooltip>
                        </Box>}
                </Box>
                <Box>
                    {onClick && typeof onClick === 'function' && <Box className="on-hover" display={{ md: "block", xs: "none" }} color={palette.text.secondary}  >
                        {Icons.default.OpenInNewIcon}
                    </Box>}
                </Box>
            </Box>}

            {/* Variant 2: With secodndary data */}
            {!loading && variant === 'stacked' && <Box height="100%" position="relative" display="flex" alignItems="stretch" flexDirection={{ md: "row", xs: "column" }}>
                <Box fontSize="20px" position="absolute" top="10px" right="10px" display="flex" alignItems="center" justifyContent="center" textAlign="center" color={palette?.[color].main}>{icon}</Box>
                <Box height="100%" padding="10px" flexGrow={1}>
                    <Box display="flex" height="100%" justifyContent="space-between" flexDirection="column" gap="10px" overflow="hidden">
                        <Box component="div">
                            <Typography maxWidth="90%" display="block" textTransform="uppercase" fontSize={"12px"} fontWeight="400" component="span" color="textSecondary">{label}</Typography>
                            <Tooltip disableInteractive placement='top-start' title={value}>
                                <Box color={palette.common.font} display="block" fontWeight="700" overflow="hidden" component="span" fontSize="20px">{value}</Box>
                            </Tooltip>
                        </Box>
                        <Box width="100%" component="div" display="flex" gap="10px" justifyContent="space-between">
                            <Typography maxWidth="90%" display="block" fontSize={"12px"} fontWeight="400" component="span" color="textSecondary">{secondaryLabel || ""}</Typography>
                            <Tooltip disableInteractive placement='top-end' title={secondary}>
                                <Box color={palette.common.font} display="block" fontWeight="700" overflow="hidden" component="span" fontSize="14px">{secondary}</Box>
                            </Tooltip>
                        </Box>
                    </Box>
                    <Box>
                        {onClick && typeof onClick === 'function' && <IconButton onClick={onClick}>
                            {Icons.default.OpenInNewIcon}
                        </IconButton>}
                    </Box>
                </Box>
            </Box>}

            {loading && variant === 'icon-first' && iconFirstSkeleton( color )}
            {loading && variant === 'stacked' && stackedSkeleton( color )}

        </Card>
    )
}

CountCard.protoType = {
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    icon: PropTypes.element.isRequired
}

export default CountCard
